import CheckIcon from "@mui/icons-material/Check";
import { Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { FacilitiesWithCredentialsQuery } from "generated-graphql/graphql";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { FACILITIES } from "./schema";

type Row = FacilitiesWithCredentialsQuery["facilities"]["items"][number];

interface SelectFacilitiesDialogProps {
  initialSelectedIds: string[];
  open: boolean;
  onSave: (selectedFacilityIds: string[]) => void;
  onCancel: () => void;
  portalJurisdiction?: string | null;
}

export const SelectFacilitiesDialog: React.FC<SelectFacilitiesDialogProps> = ({
  initialSelectedIds,
  open,
  onSave,
  onCancel,
  portalJurisdiction,
}) => {
  const { tenantId } = useParams();
  // The rows the user has selected
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedFacilityIds(initialSelectedIds);
  }, [initialSelectedIds]);

  const columns: GridColDef<Row>[] = useMemo(() => {
    return [
      {
        field: "facility",
        headerName: "Facility",
        filterKeyType: "facility",
        flex: 2,
        valueGetter: (_, row) => {
          const { name, customerFacilityId } = row;

          return `${name}${customerFacilityId ? ` (${customerFacilityId})` : ""}`;
        },
      },
      {
        field: "streetAddress1",
        headerName: "Street Address",
        flex: 2,
        sortable: true,
        valueGetter(_, row) {
          if (!row.streetAddress1) return "";

          const { streetAddress1, city, state, zip } = row;

          return [streetAddress1, city, state, zip].filter((s) => s).join(", ");
        },
      },
      {
        field: "lepc.name",
        headerName: "LEPC",
        flex: 0.5,
        valueGetter: (_, row) => row.lepc?.name,
        renderCell: ({ value }) => {
          return (
            <Tooltip title={value}>
              <Typography variant="body2">{value}</Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "hasCredential",
        headerName: "Facility Has Credential",
        flex: 1,
        align: "center",
        valueGetter: (_, row) => (row.facilityCredentials || []).length > 0,
        renderCell: ({ value }) => (value ? <CheckIcon fontSize="small" /> : null),
      },
    ];
  }, []);

  const handleSave = () => {
    onSave(selectedFacilityIds);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="lg"
      title="Select Facilities"
      description="Select one or more facilities to associate with this credential."
      primaryActionButtonProps={{
        startIcon: <CheckIcon />,
        onClick: handleSave,
      }}
    >
      <OmnisearchDataGrid
        persistenceKey="select-facilities"
        isURLDriven={false}
        columns={columns}
        dataQuery={FACILITIES}
        initialSortModel={[{ field: "facility", sort: "asc" }]}
        defaultSearch={`tenantId:${tenantId}`}
        initialFilters={
          portalJurisdiction ? [{ field: "jurisdiction", value: portalJurisdiction }] : []
        }
        getItems={(data) => data.facilities.items}
        getCount={(data) => data.facilities.count}
        noDataMessage="No facilities have been associated to this credential yet."
        initialPageSize={50}
        additionalFilterColumns={[
          { header: "Facility ID", key: "customerFacilityId" },
          { header: "City", key: "city" },
          { header: "Zip", key: "zip" },
          { header: "State", key: "state" },
          { header: "LEPC", key: "lepc.name" },
        ]}
        onSelectedIdsChanged={(ids) => setSelectedFacilityIds(ids.map((i) => i.toString()))}
        selectedIds={selectedFacilityIds}
        gridSx={{
          minHeight: "300px",
          "& .MuiDataGrid-virtualScroller": {
            minHeight: "200px",
            maxHeight: "350px",
            overflowY: "auto",
          },
        }}
        sx={{
          minWidth: 1000,
          p: 0,
        }}
        showManageColumns={false}
      />
    </Dialog>
  );
};
